import type {
  CrmCollaborationTask,
  CrmCollaborationTaskImportConfirmResult,
  CrmCollaborationTaskImportResult,
  CrmCollaborationTaskItem,
  CrmCollaborationTaskItemListPage,
  CrmCollaborationTaskListPage,
} from "../types";

type RawCounts = Partial<Record<"available" | "assigned" | "claimed" | "released" | "cancelled", number>>;

export type CrmCollaborationTaskRaw = {
  id: number;
  task_name: string;
  task_type: CrmCollaborationTask["taskType"];
  target_type: CrmCollaborationTask["targetType"];
  pool_code: string;
  assignment_mode: CrmCollaborationTask["assignmentMode"];
  task_status: CrmCollaborationTask["taskStatus"];
  owner_user_id: number;
  default_assigned_user_id?: number | null;
  worker_role_code: string;
  source_type: CrmCollaborationTask["sourceType"];
  import_id?: number | null;
  total_item_count: number;
  status_counts?: RawCounts | null;
  published_at?: string | null;
  closed_at?: string | null;
  cancelled_at?: string | null;
  created_at: string;
  updated_at: string;
  remark?: string | null;
};

export type CrmCollaborationTaskListPageRaw = {
  items: CrmCollaborationTaskRaw[];
  total: number;
  page: number;
  page_size: number;
};

export type CrmCollaborationTaskItemRaw = {
  id: number;
  task_id: number;
  task_name?: string | null;
  task_type?: CrmCollaborationTaskItem["taskType"] | null;
  target_type?: CrmCollaborationTaskItem["targetType"] | null;
  pool_code?: string | null;
  worker_role_code?: string | null;
  task_owner_user_id?: number | null;
  task_owner_user_name?: string | null;
  contact_id?: number | null;
  org_id?: number | null;
  org_unit_id?: number | null;
  original_owner_user_id?: number | null;
  assigned_user_id?: number | null;
  claimed_by_user_id?: number | null;
  target_name?: string | null;
  org_name?: string | null;
  org_unit_name?: string | null;
  original_owner_user_name?: string | null;
  assigned_user_name?: string | null;
  claimed_by_user_name?: string | null;
  access_status: CrmCollaborationTaskItem["accessStatus"];
  claimed_at?: string | null;
  released_at?: string | null;
  cancelled_at?: string | null;
  created_at: string;
  updated_at: string;
  remark?: string | null;
};

export type CrmCollaborationTaskItemListPageRaw = {
  items: CrmCollaborationTaskItemRaw[];
  total: number;
  page: number;
  page_size: number;
};

export type CrmCollaborationTaskImportResultRaw = {
  id: number;
  task_id: number;
  parsed_count: number;
  valid_count: number;
  invalid_count: number;
  duplicate_count: number;
  disabled_count: number;
  importable_count: number;
  validation_status: string;
  validation_summary_json?: { active_task_conflict_count?: number } | null;
  created_at: string;
};

export type CrmCollaborationTaskImportConfirmResultRaw = {
  inserted_count: number;
  skipped_count: number;
};

/** 映射协作任务批次响应项。 */
export function mapCrmCollaborationTask(raw: CrmCollaborationTaskRaw): CrmCollaborationTask {
  return {
    id: raw.id,
    taskName: raw.task_name,
    taskType: raw.task_type,
    targetType: raw.target_type,
    poolCode: raw.pool_code,
    assignmentMode: raw.assignment_mode,
    taskStatus: raw.task_status,
    ownerUserId: raw.owner_user_id,
    defaultAssignedUserId: raw.default_assigned_user_id ?? undefined,
    workerRoleCode: raw.worker_role_code,
    sourceType: raw.source_type,
    importId: raw.import_id ?? undefined,
    totalItemCount: raw.total_item_count,
    statusCounts: {
      available: raw.status_counts?.available ?? 0,
      assigned: raw.status_counts?.assigned ?? 0,
      claimed: raw.status_counts?.claimed ?? 0,
      released: raw.status_counts?.released ?? 0,
      cancelled: raw.status_counts?.cancelled ?? 0,
    },
    publishedAt: raw.published_at ?? undefined,
    closedAt: raw.closed_at ?? undefined,
    cancelledAt: raw.cancelled_at ?? undefined,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    remark: raw.remark ?? "",
  };
}

/** 映射协作任务批次分页响应。 */
export function mapCrmCollaborationTaskListPage(raw: CrmCollaborationTaskListPageRaw): CrmCollaborationTaskListPage {
  return {
    items: raw.items.map(mapCrmCollaborationTask),
    total: raw.total,
    page: raw.page,
    pageSize: raw.page_size,
  };
}

/** 映射协作任务对象响应项。 */
export function mapCrmCollaborationTaskItem(raw: CrmCollaborationTaskItemRaw): CrmCollaborationTaskItem {
  return {
    id: raw.id,
    taskId: raw.task_id,
    taskName: raw.task_name ?? undefined,
    taskType: raw.task_type ?? undefined,
    targetType: raw.target_type ?? undefined,
    poolCode: raw.pool_code ?? undefined,
    workerRoleCode: raw.worker_role_code ?? undefined,
    taskOwnerUserId: raw.task_owner_user_id ?? undefined,
    taskOwnerUserName: raw.task_owner_user_name ?? undefined,
    contactId: raw.contact_id ?? undefined,
    orgId: raw.org_id ?? undefined,
    orgUnitId: raw.org_unit_id ?? undefined,
    originalOwnerUserId: raw.original_owner_user_id ?? undefined,
    assignedUserId: raw.assigned_user_id ?? undefined,
    claimedByUserId: raw.claimed_by_user_id ?? undefined,
    targetName: raw.target_name ?? undefined,
    orgName: raw.org_name ?? undefined,
    orgUnitName: raw.org_unit_name ?? undefined,
    originalOwnerUserName: raw.original_owner_user_name ?? undefined,
    assignedUserName: raw.assigned_user_name ?? undefined,
    claimedByUserName: raw.claimed_by_user_name ?? undefined,
    accessStatus: raw.access_status,
    claimedAt: raw.claimed_at ?? undefined,
    releasedAt: raw.released_at ?? undefined,
    cancelledAt: raw.cancelled_at ?? undefined,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    remark: raw.remark ?? "",
  };
}

/** 映射协作任务对象分页响应。 */
export function mapCrmCollaborationTaskItemListPage(
  raw: CrmCollaborationTaskItemListPageRaw,
): CrmCollaborationTaskItemListPage {
  return {
    items: raw.items.map(mapCrmCollaborationTaskItem),
    total: raw.total,
    page: raw.page,
    pageSize: raw.page_size,
  };
}

/** 映射任务对象导入校验结果。 */
export function mapCrmCollaborationTaskImportResult(
  raw: CrmCollaborationTaskImportResultRaw,
): CrmCollaborationTaskImportResult {
  return {
    id: raw.id,
    taskId: raw.task_id,
    parsedCount: raw.parsed_count,
    validCount: raw.valid_count,
    invalidCount: raw.invalid_count,
    duplicateCount: raw.duplicate_count,
    disabledCount: raw.disabled_count,
    importableCount: raw.importable_count,
    activeTaskConflictCount: raw.validation_summary_json?.active_task_conflict_count ?? 0,
    validationStatus: raw.validation_status,
    createdAt: raw.created_at,
  };
}

/** 映射任务对象确认导入结果。 */
export function mapCrmCollaborationTaskImportConfirmResult(
  raw: CrmCollaborationTaskImportConfirmResultRaw,
): CrmCollaborationTaskImportConfirmResult {
  return {
    insertedCount: raw.inserted_count,
    skippedCount: raw.skipped_count,
  };
}
